import { useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { useGetAgeClassifications } from "../../services/ageClassificationService";
import {
  useCreateSwimmersBatch,
  useGetSwimmerById,
  swimmerQueryKeys,
  getDefaultSwimmersListParams,
} from "../../services/swimmerService";
import type {
  SwimmerBatchItem,
  GetSwimmersCountRequest,
} from "../../../../api/client/types/swimmer.types";
import type { PaginatedResponse } from "../../../../api/client/types/common.types";
import type { Swimmer } from "../../../../api/client/types/swimmer.types";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../../../routes/routes";
import { useGetSwimmerTypes } from "../../services/swimmerTypeService";
import { useAuthStore } from "../../../../store";

export const useRegisterSwimmersPage = () => {
  const queryClient = useQueryClient();
  const { swimmerId } = useParams<{ swimmerId?: string }>();
  const { user } = useAuthStore();
  const buildingId = user?.buildingId;
  const lifeguardId = user?.id;
  const { data: ageClassifications } = useGetAgeClassifications();
  const { data: swimmerTypes } = useGetSwimmerTypes();
  const { mutate: createSwimmersBatch, isPending: isSubmitting } =
    useCreateSwimmersBatch();
  const navigate = useNavigate();

  const todayDateStr = useMemo(() => {
    const now = new Date();
    const colombiaDateStr = now.toLocaleDateString("en-CA", {
      timeZone: "America/Bogota",
    });
    const [year, month, day] = colombiaDateStr.split("-");
    return `${day}/${month}/${year}`;
  }, []);

  // Fetch swimmer data if swimmerId is provided in URL
  const hasSwimmerId = !!swimmerId;
  const {
    data: initialSwimmer,
    isLoading: isLoadingSwimmer,
    isError: isErrorSwimmer,
  } = useGetSwimmerById(swimmerId || "");

  const handleNavigateToDashboard = () => {
    navigate(ROUTES.DASHBOARD.LIFEGUARD);
  };

  const handleCreateSwimmersBatch = (swimmers: SwimmerBatchItem[]) => {
    createSwimmersBatch(
      { swimmers },
      {
        onSuccess: (response) => {
          const listParams = getDefaultSwimmersListParams({
            building_id: buildingId,
            lifeguard_id: lifeguardId,
          });
          queryClient.setQueryData<PaginatedResponse<Swimmer>>(
            swimmerQueryKeys.list(listParams),
            (old) => {
              if (!old) return old;
              // Batch returns request order (oldest first); list API uses created_at desc (newest first)
              const batchNewestFirst = [...response.swimmers].reverse();
              const newItems = [...batchNewestFirst, ...old.items].slice(
                0,
                old.page_size
              );
              const newTotal = old.total + response.count;
              const newTotalPages = Math.ceil(newTotal / old.page_size);
              return {
                ...old,
                items: newItems,
                total: newTotal,
                total_pages: newTotalPages,
              };
            }
          );

          const countParams: GetSwimmersCountRequest = {
            building_id: buildingId ?? undefined,
            lifeguard_id: lifeguardId ?? undefined,
            start_date: todayDateStr,
            end_date: todayDateStr,
          };
          queryClient.setQueryData(
            swimmerQueryKeys.count(countParams),
            (prev: { count: number } | undefined) => ({
              count: (prev?.count ?? 0) + response.count,
            })
          );

          toast.success("Usuarios creados correctamente!");
          handleNavigateToDashboard();
        },
      }
    );
  };

  return {
    ageClassifications: ageClassifications ?? [],
    swimmerTypes: swimmerTypes ?? [],
    handleCreateSwimmersBatch,
    handleNavigateToDashboard,
    isSubmitting,
    initialSwimmer,
    // Only show loading/error states if we're actually trying to load a swimmer
    isLoadingSwimmer: hasSwimmerId && isLoadingSwimmer,
    isErrorSwimmer: hasSwimmerId && isErrorSwimmer,
  };
};
